import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpHandler,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { Observable, pipe, of, from, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { UserLoggedIn } from '../models/user-login.model';
import { CurrentUserService } from './current-user.service';
import { StreamInvocationMessage } from '@microsoft/signalr';

@Injectable({
  providedIn: 'root',
})
export class BaseHttpServiceService {
  private currentUser: string = 'currentUser';
  User: UserLoggedIn | undefined;
  constructor(
    protected _http: HttpClient,
    private _translateService: TranslateService,
    private _currentUserService: CurrentUserService
  ) {}

  protected httpOptions = {
    headers: new HttpHeaders({
      'content-type': 'application/json',
      language: 'en',
    }),
  };

  protected setHeaders(): void {
    let currentLang = this._translateService.currentLang;
    let currentUser = localStorage.getItem(this.currentUser);
    this.User = currentUser != null ? JSON.parse(currentUser) : '';
    if (this.User) {
      this.httpOptions = {
        headers: new HttpHeaders({
          'content-type': 'application/json',
          language: currentLang,
          Authorization: this.User.token_type + ' ' + this.User.access_Token,
        }),
      };
    } else {
      this.httpOptions = {
        headers: new HttpHeaders({
          'content-type': 'application/json',
          language: currentLang,
        }),
      };
    }
  }

  protected getData<T>(url: string, options?: any): Observable<T> {
    this.setHeaders();
    return this._http.get<T>(url, this.httpOptions).pipe(
      map(result => {
        const camelCaseResult = this.toCamel(result); // Convert to camel case
        return camelCaseResult as T; // Return the transformed result
      }),
      catchError(this.handleError<T>(`getData`))
    );
  }

  protected getTextData(url: string): Observable<string> {
    this.setHeaders();
    const textOptions = {
      headers: this.httpOptions.headers,
      responseType: 'text' as const,
    };

    return this._http
      .get(url, textOptions)
      .pipe(catchError(this.handleError<string>(`getTextData`)));
  }
  protected postTextData(url: string, body: unknown): Observable<string> {
    this.setHeaders();
    const textOptions = {
      headers: this.httpOptions.headers,
      responseType: 'text' as const,
    };
    return this._http
      .post(url, body, textOptions)
      .pipe(catchError(this.handleError<string>('postTextData')));
  }

  protected postData<T>(
    url: string,
    data: any,
    options?: { params?: HttpParams }
  ): Observable<T> {
    this.setHeaders();
    const requestOptions = { ...this.httpOptions, ...options };
    return this._http.post<T>(url, data, requestOptions).pipe(
      map(result => {
        const camelCaseResult = this.toCamel(result); // Convert to camel case
        return camelCaseResult as T; // Return the transformed result
      }),
      catchError(this.handleError<T>(`postData`))
    );
  }

  toCamel(o: any) {
    // If it's a primitive value (string, number, boolean), return it as is
    if (typeof o !== 'object' || o === null) {
      return o;
    }

    let newO: any;
    if (Array.isArray(o)) {
      return o.map(value => {
        if (typeof value === 'object') {
          value = this.toCamel(value);
        }
        return value;
      });
    } else {
      newO = {};
      for (const origKey in o) {
        if (o.hasOwnProperty(origKey)) {
          const newKey = (
            origKey.charAt(0).toLowerCase() + origKey.slice(1) || origKey
          ).toString();

          let value = o[origKey];
          if (
            Array.isArray(value) ||
            (value !== null && typeof value === 'object')
          ) {
            value = this.toCamel(value);
          }

          // ✅ Only overwrite if newO[newKey] is empty/null/undefined
          if (
            newO[newKey] === undefined ||
            newO[newKey] === null ||
            newO[newKey] === '' // optional: treat empty string as "no value"
          ) {
            newO[newKey] = value;
          }
        }
      }
    }
    return newO;
  }

  protected putData<T>(url: string, data: T): Observable<T> {
    this.setHeaders();
    return this._http.put<T>(url, data, this.httpOptions).pipe(
      map(result => {
        const camelCaseResult = this.toCamel(result); // Convert to camel case
        return camelCaseResult as T; // Return the transformed result
      }),
      catchError(this.handleError<T>(`putData`))
    );
  }

  protected exportData<T>(url: string, model: T) {
    this.setHeaders();
    return this._http
      .post(url, model, {
        responseType: 'blob',
        headers: this.httpOptions.headers,
      })
      .pipe(
        tap(data => console.log('export data: ' + data)),
        catchError(this.handleError<any>(`exportData`))
      );
  }
  protected download<T>(url: string): Observable<T> {
    this.setHeaders();
    return this._http
      .get(url, { responseType: 'blob', headers: this.httpOptions.headers })
      .pipe(
        map(response => response as T), // Explicitly cast Blob to the expected type
        catchError(error => this.handleBlobError<T>(error)) // Ensure correct error handling
      );
  }

  handleBlobError<T>(error: HttpErrorResponse): Observable<T> {
    if (error.error instanceof Blob) {
      return from(error.error.text()).pipe(
        switchMap(text => {
          try {
            const errorJson = JSON.parse(text);
            console.error('Parsed Error:', errorJson);
            return throwError(() => errorJson as T); // Ensures correct type assignment
          } catch (e) {
            console.error('Error parsing Blob response:', e);
            return throwError(() => new Error('Unknown error occurred') as T);
          }
        })
      );
    }

    return throwError(() => error);
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      if (error.status == 401) {
        this._currentUserService.logOut();
        return new Observable();
      } else {
        // TODO: send the error to remote logging infrastructure
        //console.error(JSON.stringify(error)); // log to console instead

        // TODO: better job of transforming error for user consumption
        //this.log(`${operation} failed: ${error.message}`);

        // Let the app keep running by returning an empty result.
        //return of(result as T);
        throw error;
      }
    };
  }

  protected postDataUpload<T>(url: string, data: any): Observable<T> {
    let currentLang = this._translateService.currentLang;
    let currentUser = localStorage.getItem(this.currentUser);
    this.User = currentUser != null ? JSON.parse(currentUser) : '';
    if (this.User) {
      this.httpOptions = {
        headers: new HttpHeaders({
          language: currentLang,
          'Access-Control-Allow-Origin': '*',
          Authorization: this.User.token_type + ' ' + this.User.access_Token,
        }),
      };
    } else {
      this.httpOptions = {
        headers: new HttpHeaders({
          'Access-Control-Allow-Origin': '*',
          'content-type': 'application/json',
          language: currentLang,
        }),
      };
    }
    return this._http.post<T>(url, data, this.httpOptions).pipe(
      tap(result => {
        // console.log(`postData() => Data fetched.`);
      }),
      catchError(this.handleError<T>(`postData`))
    );
  }
  // protected oldBaseUrl: string = `http://41.38.102.53:4422/`;
  protected baseUrl: string = `${environment.APIUrl}/api/pt`;
  protected mysteryShopperUrl: string = `${environment.APIUrl}/api`;
  protected publicAuthUrl: string = `${environment.PublicAuthUrl}/api`;
  domainUrl: string = environment.APIUrl;
}
